/**
 * Mic Mute Registry
 *
 * A module-level singleton that bridges apiFetch (a pure utility) with the
 * AudioRecorder instance that lives inside ControlTray. ControlTray registers
 * its muteMic/unmuteMic callbacks here on mount; apiFetch reads them to
 * silence the mic for the duration of every .NET API call.
 */

let _muteFn: (() => void) | null = null;
let _unmuteFn: (() => void) | null = null;
let _isUserMutedFn: (() => boolean) | null = null;
let _apiMuteDepth = 0;
let _wasUserMutedWhenApiMuteStarted = false;
let _phaseMuteActive = false;
let _wasUserMutedWhenPhaseMuteStarted = false;

/**
 * Called by ControlTray after the AudioRecorder is ready.
 * Stores references to muteMic / unmuteMic so apiFetch can invoke them.
 */
export function registerMicControls(
  muteFn: () => void,
  unmuteFn: () => void,
  isUserMutedFn: () => boolean,
): void {
  _muteFn = muteFn;
  _unmuteFn = unmuteFn;
  _isUserMutedFn = isUserMutedFn;
}

/**
 * Called by ControlTray on unmount cleanup.
 * Clears the stored references so stale callbacks are never invoked.
 */
export function unregisterMicControls(): void {
  _muteFn = null;
  _unmuteFn = null;
  _isUserMutedFn = null;
  _apiMuteDepth = 0;
  _wasUserMutedWhenApiMuteStarted = false;
  _phaseMuteActive = false;
  _wasUserMutedWhenPhaseMuteStarted = false;
}

/**
 * Mute the mic if controls are registered (no-op otherwise).
 * Called by apiFetch before issuing a .NET API request.
 */
export function muteMicForApi(): void {
  if (_apiMuteDepth === 0) {
    _wasUserMutedWhenApiMuteStarted = _isUserMutedFn?.() ?? false;
    if (!_wasUserMutedWhenApiMuteStarted) {
      _muteFn?.();
    }
  }
  _apiMuteDepth += 1;
}

/**
 * Unmute the mic if controls are registered (no-op otherwise).
 * Called by apiFetch in a finally block after a .NET API request completes.
 */
export function unmuteMicForApi(): void {
  if (_apiMuteDepth <= 0) {
    return;
  }

  _apiMuteDepth -= 1;
  if (_apiMuteDepth > 0) {
    return;
  }

  // Do NOT unmute if a phase-level mute is active (e.g. document upload phase)
  if (_phaseMuteActive) {
    return;
  }

  const isCurrentlyUserMuted = _isUserMutedFn?.() ?? false;
  if (!_wasUserMutedWhenApiMuteStarted && !isCurrentlyUserMuted) {
    _unmuteFn?.();
  }
  _wasUserMutedWhenApiMuteStarted = false;
}

// ── Phase-level mute (e.g. entire document upload phase) ────────────

/**
 * Mute the mic for the entire duration of a UI phase (e.g. document upload).
 * Unlike API-level mute, this persists until explicitly unphased.
 */
export function muteMicForPhase(): void {
  if (_phaseMuteActive) return; // already in a phase mute
  _phaseMuteActive = true;
  _wasUserMutedWhenPhaseMuteStarted = _isUserMutedFn?.() ?? false;
  if (!_wasUserMutedWhenPhaseMuteStarted) {
    _muteFn?.();
  }
}

/**
 * Unmute the mic after a UI phase ends.
 * Only unmutes if the user hadn't manually muted before the phase started.
 */
export function unmuteMicForPhase(): void {
  if (!_phaseMuteActive) return;
  _phaseMuteActive = false;

  // If an API mute is still in progress, don't unmute — let the API handler handle it
  if (_apiMuteDepth > 0) return;

  const isCurrentlyUserMuted = _isUserMutedFn?.() ?? false;
  if (!_wasUserMutedWhenPhaseMuteStarted && !isCurrentlyUserMuted) {
    _unmuteFn?.();
  }
  _wasUserMutedWhenPhaseMuteStarted = false;
}
